import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingMC from '../../../components/LoadingMC';
import IconMC from '../../../components/IconMC';
import useDocumentMessageData from '../../../hooks/inbox/data/useDocumentMessageData';
import useDocumentMessageUI from '../../../hooks/inbox/ui/useDocumentMessageUI';

/**
 * @typedef {Object} DocumentData
 * @property {string} [contentMediaUrl] - URL del documento para descarga
 * @property {string} [documentUrl] - URL alternativa del documento
 * @property {string} [fileUrl] - URL de archivo genérica
 * @property {string} [id] - ID único del documento/mensaje
 * @property {boolean} [inbound] - Si el documento es entrante o saliente
 */

/**
 * @typedef {Object} DocumentMCProps
 * @property {DocumentData} data - Objeto con datos del documento
 * @property {string} platform - Plataforma ('whatsapp', 'messenger', 'instagram')
 * @property {string} [fileName] - Nombre del archivo a mostrar
 * @property {string} [className] - Clases CSS adicionales para el enlace
 * @property {string} [iconName='file-download-outline'] - Nombre del ícono a mostrar
 * @property {string} [iconClassName] - Clases CSS adicionales para el ícono
 * @property {string} [urlField='contentMediaUrl'] - Campo del objeto data que contiene la URL
 */

/**
 * Componente atómico optimizado para renderizar documentos descargables en el inbox.
 * 
 * Implementa separación estricta de capas usando hooks especializados para datos y UI.
 * Proporciona funcionalidad de descarga con estados de loading, error y éxito,
 * soporte multi-plataforma y accesibilidad completa.
 * 
 * @component
 * @memberof InboxComponents
 * @category Atoms
 * 
 * @description Arquitectura de separación de capas implementada:
 * - **Data Layer**: useDocumentMessageData - Maneja carga de URL, loading y error states
 * - **UI Layer**: useDocumentMessageUI - Maneja clases CSS, handlers y lógica de UI
 * - **Presentation**: Componente puro que orquesta ambas capas
 * 
 * @description Características principales:
 * - Separación estricta de responsabilidades entre data y UI
 * - Loading states y error handling robusto
 * - Soporte multi-plataforma con URLs adaptativas
 * - Memoización completa para optimizar performance
 * - Accesibilidad con ARIA labels y estructura semántica
 * - Internacionalización completa con react-i18next
 * 
 * @param {DocumentMCProps} props - Props del componente
 * @param {DocumentData} props.data - Datos del documento con URLs y metadatos
 * @param {string} props.platform - Plataforma de origen del documento
 * @param {string} [props.fileName] - Nombre del archivo a mostrar en el enlace
 * @param {string} [props.className] - Clases CSS adicionales para customización
 * @param {string} [props.iconName='file-download-outline'] - Ícono del documento
 * @param {string} [props.iconClassName] - Clases CSS del ícono
 * @param {string} [props.urlField='contentMediaUrl'] - Campo que contiene la URL
 * 
 * @returns {React.ReactElement} Componente de documento descargable
 * 
 * @example
 * // Uso básico con documento de WhatsApp
 * ```jsx
 * import DocumentMC from 'presentation/modules/inbox/components/DocumentMC';
 * 
 * const documentData = {
 *   contentMediaUrl: 'https://storage.example.com/document.pdf',
 *   id: 'doc-123',
 *   inbound: true
 * };
 * 
 * <DocumentMC 
 *   data={documentData}
 *   platform="whatsapp"
 *   fileName="Factura_2024.pdf"
 * />
 * ```
 * 
 * @example
 * // Uso avanzado con customización de ícono y clases
 * ```jsx
 * <DocumentMC 
 *   data={documentData}
 *   platform="messenger"
 *   fileName="Contrato.pdf"
 *   className="tw-bg-blue-100 tw-rounded-lg tw-p-4"
 *   iconName="file-pdf-outline"
 *   iconClassName="tw-text-red-500 tw-text-lg"
 *   urlField="documentUrl"
 * />
 * ```
 * 
 * @example
 * // Documento con URL alternativa
 * ```jsx
 * const customDocument = {
 *   documentUrl: 'https://api.example.com/files/doc-456',
 *   fileType: 'application/pdf',
 *   size: 1024000
 * };
 * 
 * <DocumentMC 
 *   data={customDocument}
 *   platform="instagram"
 *   urlField="documentUrl"
 *   fileName="Manual_Usuario.pdf"
 * />
 * ```
 * 
 * @performance
 * - React.memo previene re-renders innecesarios
 * - Hooks memoizados evitan recálculo de clases y handlers
 * - Loading states optimizados para mejor UX
 * - Lazy loading de URLs pesadas
 * 
 * @accessibility
 * - Enlace semánticamente correcto con href y onClick
 * - ARIA labels automáticos basados en fileName
 * - Soporte completo para screen readers
 * - Contraste y tamaños apropiados para iconos
 * 
 * @i18n
 * - Textos completamente traducibles con react-i18next
 * - Fallbacks apropiados para nombres de archivo
 * - Mensajes de error localizados
 * 
 * @see {@link useDocumentMessageData} Hook de capa de datos
 * @see {@link useDocumentMessageUI} Hook de capa de UI
 * @see {@link LoadingMC} Componente de loading state
 * @see {@link IconMC} Componente de iconografía
 * 
 * @since 1.0.0
 * @version 2.0.0 - Refactored with layer separation and optimized performance
 */
const DocumentMC = memo(({ data, platform, fileName, className, iconName = "file-download-outline", iconClassName, urlField = 'contentMediaUrl' }) => {
  const { t } = useTranslation('InboxMC');
  
  /**
   * Hook de capa de datos - Maneja la obtención de URL y estados de carga.
   * 
   * Responsabilidades:
   * - Extracción de URL desde el campo especificado
   * - Estados de loading durante la obtención de datos
   * - Manejo de errores de red o datos inválidos
   * - Memoización para evitar refetch innecesario
   * 
   * @type {Object}
   * @property {string|null} url - URL procesada del documento
   * @property {boolean} loading - Estado de carga activa
   * @property {boolean} error - Estado de error en la obtención
   */
  const { url, loading, error } = useDocumentMessageData({ data, platform, urlField });
  
  /**
   * Hook de capa de UI - Maneja lógica visual y handlers de interacción.
   * 
   * Responsabilidades:
   * - Generación de clases CSS memoizadas para enlace e ícono
   * - Handler optimizado para descarga con preventDefault
   * - Lógica de presentación sin dependencias de datos
   * - Memoización de funciones para evitar re-renders
   * 
   * @type {Object}
   * @property {Function} getLinkClass - Función que retorna clases CSS del enlace
   * @property {Function} getIconClass - Función que retorna clases CSS del ícono
   * @property {Function} handleDownload - Handler para evento de descarga
   */
  const { getLinkClass, getIconClass, handleDownload } = useDocumentMessageUI({ className, iconClassName, url, fileName });

  return (
    <>
      <LoadingMC loading={loading} />
      {error ? (
        <blockquote className="tw-text-center tw-mt-20 tw-text-xs">
          {t('file.not_available')}
        </blockquote>
      ) : (
        <a
          href=""
          className={getLinkClass()}
          onClick={handleDownload}
          role="button"
          tabIndex={0}
          aria-label={fileName ? `${t('file.download')} ${fileName}` : t('file.download')}
        >
          <IconMC name={iconName} className={getIconClass()} />
          {fileName || t('file.download')}
        </a>
      )}
    </>
  );
});

DocumentMC.displayName = 'DocumentMC';

export default DocumentMC;
